import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, collection, getDocs, query, where } from "firebase/firestore";
import {
  Box, Typography, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Grid, Card, Stack, Chip, CircularProgress, Avatar, Divider,
  Container
} from "@mui/material";
import {
  Login, Logout, AccessTime, Badge, CalendarMonth, LocationOn
} from "@mui/icons-material";
import { format, differenceInMinutes, isAfter, setHours, setMinutes } from "date-fns";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";
const BG = "#f8fafc";
const DARK = "#0f172a";
const SECONDARY = "#64748b";

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [salaryConfig, setSalaryConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Attendance Matrix State
  const [attendanceData, setAttendanceData] = useState({});
  const [viewDate, setViewDate] = useState(new Date());
  const [todayStatus, setTodayStatus] = useState(null);
  const [counts, setCounts] = useState({ present: 0, absent: 0, halfDay: 0, late: 0, salaryEarned: 0 });

  // Dialog Control
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const todayDateStr = format(new Date(), "yyyy-MM-dd");

  const legendItems = [
    { label: "Present", color: "#e8f5e9", border: "#a5d6a7" },
    { label: "Late Mark", color: "#fff3e0", border: "#ffcc80" },
    { label: "Half Day", color: "#e3f2fd", border: "#bbdefb" },
    { label: "Absent", color: "#ffebee", border: "#ef9a9a" },
  ];

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "authroles", currentUser.uid));
        if (!userDoc.exists()) {
          Toast.fire({ icon: "error", title: "Profile configuration records missing." });
          auth.signOut();
          navigate("/login");
          return;
        }
        const profileData = userDoc.data();
        setUser({ uid: currentUser.uid, ...profileData });

        const salaryDoc = await getDoc(doc(db, "salaryAllocations", currentUser.uid));
        if (salaryDoc.exists()) {
          setSalaryConfig(salaryDoc.data());
        }

        await loadPersonalAttendance(currentUser.uid, salaryDoc.exists() ? salaryDoc.data() : null);

      } catch (err) {
        console.error("Initialization workflow broken:", err);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [navigate, viewDate]);

  const loadPersonalAttendance = async (uid, salaryMeta) => {
    try {
      const q = query(collection(db, "staffattendance"), where("uid", "==", uid));
      const snap = await getDocs(q);

      let map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.date) {
          map[data.date] = { docId: d.id, ...data };
        }
      });
      setAttendanceData(map);

      if (map[todayDateStr]) {
        if (map[todayDateStr].checkOutTime) setTodayStatus("CheckedOut");
        else if (map[todayDateStr].checkInTime) setTodayStatus("CheckedIn");
      } else {
        setTodayStatus(null);
      }

      calculateStats(map, salaryMeta);
    } catch (err) {
      console.error("Failed downloading attendance records:", err);
    }
  };

  const calculateStats = (map, salaryMeta) => {
    const currentMonthStr = format(viewDate, "yyyy-MM");
    let p = 0, a = 0, h = 0, l = 0;

    Object.values(map).forEach(r => {
      if (r.date && r.date.startsWith(currentMonthStr)) {
        if (r.status === "Present") p++;
        else if (r.status === "Absent") a++;
        else if (r.status === "Half Day") h++;
        else if (r.status === "Late Mark") l++;
      }
    });

    const baseSalary = Number(salaryMeta?.baseSalary || salaryMeta?.totalSalary || 0);
    const perDay = baseSalary / 30;
    const earned = ((p + l) * perDay) + (h * 0.5 * perDay);

    setCounts({ present: p, absent: a, halfDay: h, late: l, salaryEarned: Math.round(earned) });
  };

  // Geolocation Grabber Helper
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
      } else {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          }),
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    });
  };

  const handlePunchAction = async (type) => {
    setActionLoading(true);
    const docId = `${user.uid}_${todayDateStr}`;
    const docRef = doc(db, "staffattendance", docId);
    const now = new Date();
    const nowStr = now.toISOString();

    try {
      if (type === "IN") {
        // 1. Fetch Location Coordinates
        let locationData = { latitude: null, longitude: null, locationStr: "Location Unavailable" };
        try {
          const coords = await getCurrentLocation();
          locationData = {
            latitude: coords.latitude,
            longitude: coords.longitude,
            locationStr: `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`
          };
        } catch (locErr) {
          console.warn("Location prompt denied or unavailable:", locErr);
          Toast.fire({ icon: "warning", title: "Location services disabled or timed out." });
        }

        // 2. Late Mark check rule: After 11:30 AM
        const lateLimit = setMinutes(setHours(new Date(), 11), 30); // 11:30 AM
        const finalStatus = isAfter(now, lateLimit) ? "Late Mark" : "Present";

        await setDoc(docRef, {
          uid: user.uid,
          staffId: user.uid,
          staffName: user.name || "Staff Member",
          role: user.role || "Staff",
          date: todayDateStr,
          status: finalStatus,
          checkInTime: nowStr,
          checkOutTime: null,
          checkInLatitude: locationData.latitude,
          checkInLongitude: locationData.longitude,
          checkInLocation: locationData.locationStr,
          updatedAt: nowStr
        }, { merge: true });

        Toast.fire({ icon: "success", title: `Checked In: Status set to ${finalStatus}` });
      } else {
        // CHECK-OUT LOGIC
        const recordSnap = await getDoc(docRef);
        let finalStatus = "Present";

        if (recordSnap.exists()) {
          const data = recordSnap.data();
          
          // Rule: Half day if check-out happens before 5:00 PM (17:00) OR total work is under 4 hrs
          const fivePM = setMinutes(setHours(new Date(), 17), 0); // 5:00 PM
          const isBeforeFivePM = !isAfter(now, fivePM);

          if (data.checkInTime) {
            const checkInDate = new Date(data.checkInTime);
            const workMins = differenceInMinutes(now, checkInDate);

            if (isBeforeFivePM || (workMins > 0 && workMins < 240)) {
              finalStatus = "Half Day";
            } else {
              finalStatus = data.status || "Present";
            }
          } else if (isBeforeFivePM) {
            finalStatus = "Half Day";
          }
        }

        await setDoc(docRef, {
          checkOutTime: nowStr,
          status: finalStatus,
          updatedAt: nowStr
        }, { merge: true });

        Toast.fire({ icon: "success", title: `Checked Out successfully as ${finalStatus}.` });
      }

      await loadPersonalAttendance(user.uid, salaryConfig);
    } catch (err) {
      console.error("Error executing punch transaction:", err);
      Toast.fire({ icon: "error", title: "Operational error during punch action." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleTileDetailsInspection = (date) => {
    const dStr = format(date, "yyyy-MM-dd");
    const match = attendanceData[dStr];

    setSelectedDate(date);
    setSelectedRecord(match || null);
    setOpen(true);
  };

  // Safe time parser supporting strings and Firestore timestamps
  const formatTimeStr = (timeVal) => {
    if (!timeVal) return "--:--";
    const dateObj = timeVal?.toDate ? timeVal.toDate() : new Date(timeVal);
    return isNaN(dateObj.getTime()) ? "--:--" : dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getDurationStr = (inTime, outTime) => {
    if (!inTime || !outTime) return "--";
    const inDate = inTime?.toDate ? inTime.toDate() : new Date(inTime);
    const outDate = outTime?.toDate ? outTime.toDate() : new Date(outTime);
    if (isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return "--";

    const mins = differenceInMinutes(outDate, inDate);
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh", justifyContent: "center", alignItems: "center", background: BG }}>
        <CircularProgress style={{ color: PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box sx={{ background: BG, minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg" disableGutters>

        {/* Employee Header Profile Card */}
        <Card sx={{ p: 3, borderRadius: 5, border: "1px solid #e2e8f0", bgcolor: "#fff", mb: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.01)" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Avatar sx={{ bgcolor: `${PRIMARY}15`, color: PRIMARY, width: 64, height: 64, borderRadius: 4, fontWeight: "bold", fontSize: 24 }}>
                  {user?.name?.[0] || "U"}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={900} color={DARK} sx={{ letterSpacing: "-0.5px" }}>
                    {user?.name || "Employee Profile"}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" mt={0.5}>
                    <Chip size="small" icon={<Badge style={{ fontSize: 16, color: PRIMARY }} />} label={user?.role || "Staff"} sx={{ bgcolor: `${PRIMARY}10`, color: PRIMARY, fontWeight: 700, borderRadius: 1.5 }} />
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              {actionLoading ? (
                <CircularProgress size={32} sx={{ color: PRIMARY, mx: 4 }} />
              ) : (
                <Stack direction="row" spacing={2} width="100%" justifyContent={{ xs: "stretch", md: "flex-end" }}>
                  <Button
                    variant="contained"
                    disabled={todayStatus === "CheckedIn" || todayStatus === "CheckedOut"}
                    onClick={() => handlePunchAction("IN")}
                    startIcon={<Login />}
                    sx={{ px: 4, py: 1.5, borderRadius: 3, fontWeight: "bold", textTransform: "none", boxShadow: "none", bgcolor: PRIMARY, "&:hover": { bgcolor: "#0f766e" } }}
                  >
                    Check In
                  </Button>
                  <Button
                    variant="contained"
                    disabled={todayStatus !== "CheckedIn"}
                    onClick={() => handlePunchAction("OUT")}
                    startIcon={<Logout />}
                    sx={{ px: 4, py: 1.5, borderRadius: 3, fontWeight: "bold", textTransform: "none", boxShadow: "none", bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}
                  >
                    Check Out
                  </Button>
                </Stack>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5, borderColor: "#f1f5f9" }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTime sx={{ color: SECONDARY, fontSize: 18 }} />
            <Typography variant="body2" color={SECONDARY} fontWeight={500}>Today Status Window:</Typography>
            {!todayStatus && <Chip label="No Punch Found" size="small" sx={{ bgcolor: "#f1f5f9", color: DARK, fontWeight: 700 }} />}
            {todayStatus === "CheckedIn" && <Chip label="Active Shift" size="small" sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 700 }} />}
            {todayStatus === "CheckedOut" && <Chip label="Shift Concluded" size="small" sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 700 }} />}
          </Stack>
        </Card>

        {/* Dashboard Grid Splitter */}
        <Grid container spacing={3}>

          {/* Left Metrics Track Analytics */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle2" fontWeight="800" color={DARK} mb={2}>Matrix Legends</Typography>
                <Grid container spacing={1}>
                  {legendItems.map((item) => (
                    <Grid item xs={6} key={item.label}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: item.color, border: `1px solid ${item.border}`, textAlign: "center" }}>
                        <Typography variant="caption" fontWeight="bold" color="textSecondary">{item.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Card sx={{ p: 3, bgcolor: "#1e1b4b", color: "white", borderRadius: 5, boxShadow: "none" }}>
                <Grid container spacing={1} textAlign="center">
                  <Grid item xs={3}>
                    <Typography variant="h6" fontWeight="bold">{counts.present}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Present</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" fontWeight="bold" color="#fbbf24">{counts.late}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Late</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" fontWeight="bold" color="#60a5fa">{counts.halfDay}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Half</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" fontWeight="bold" color="#f87171">{counts.absent}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Absent</Typography>
                  </Grid>
                </Grid>
              </Card>
            </Stack>
          </Grid>

          {/* Right Interactive Calendar */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 5, bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
              <Typography variant="h6" fontWeight={800} color={DARK} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonth sx={{ color: PRIMARY }} /> Visual Shift Log Matrix
              </Typography>
              <Calendar
                onClickDay={handleTileDetailsInspection}
                onActiveStartDateChange={({ activeStartDate }) => setViewDate(activeStartDate)}
                tileClassName={({ date, view }) => {
                  if (view !== "month") return;
                  const dStr = format(date, "yyyy-MM-dd");
                  const res = attendanceData[dStr]?.status;
                  if (res === "Present") return "present-tile";
                  if (res === "Absent") return "absent-tile";
                  if (res === "Half Day") return "halfday-tile";
                  if (res === "Late Mark") return "late-tile";
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Detail Inspection Dialog */}
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ fontWeight: 800, color: DARK }}>
            Log Details: {selectedDate ? format(selectedDate, "dd MMMM yyyy") : ""}
          </DialogTitle>
          <DialogContent dividers sx={{ py: 3 }}>
            {selectedRecord ? (
              <Stack spacing={2.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600} color={SECONDARY}>Status Classification:</Typography>
                  <Chip
                    label={selectedRecord.status}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: selectedRecord.status === "Present" ? "#e8f5e9" : selectedRecord.status === "Late Mark" ? "#fff3e0" : selectedRecord.status === "Half Day" ? "#e3f2fd" : "#ffebee",
                      color: selectedRecord.status === "Present" ? "#2e7d32" : selectedRecord.status === "Late Mark" ? "#ed6c02" : selectedRecord.status === "Half Day" ? "#1976d2" : "#d32f2f"
                    }}
                  />
                </Stack>
                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3, border: "1px solid #e2e8f0" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={SECONDARY} display="block">PUNCH IN TIMESTAMP</Typography>
                      <Typography variant="body2" fontWeight={700} color={DARK} sx={{ fontFamily: "monospace", mt: 0.5 }}>
                        {formatTimeStr(selectedRecord.checkInTime || selectedRecord.checkIn)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color={SECONDARY} display="block">PUNCH OUT TIMESTAMP</Typography>
                      <Typography variant="body2" fontWeight={700} color={DARK} sx={{ fontFamily: "monospace", mt: 0.5 }}>
                        {formatTimeStr(selectedRecord.checkOutTime || selectedRecord.checkOut)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Geolocation Section */}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn sx={{ color: PRIMARY, fontSize: 18 }} />
                    <Box>
                      <Typography variant="caption" color={SECONDARY} display="block" fontWeight={600}>
                        CHECK-IN LOCATION
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color={DARK}>
                        {selectedRecord.checkInLocation || 
                          (selectedRecord.checkInLatitude ? `${selectedRecord.checkInLatitude.toFixed(4)}, ${selectedRecord.checkInLongitude.toFixed(4)}` : "Not Recorded")}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={1}>
                  <Typography variant="body2" fontWeight={600} color={SECONDARY}>Calculated Total Shift Duration:</Typography>
                  <Typography variant="body1" fontWeight={800} color={DARK}>
                    {getDurationStr(selectedRecord.checkInTime || selectedRecord.checkIn, selectedRecord.checkOutTime || selectedRecord.checkOut)}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color={SECONDARY} align="center" py={2}>
                No attendance or punch data recorded for this date.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)} variant="outlined" sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: "bold", color: SECONDARY, borderColor: "#e2e8f0" }}>
              Dismiss
            </Button>
          </DialogActions>
        </Dialog>

      </Container>

      {/* Calendar Styling Rules */}
      <style>{`
        .react-calendar { width: 100% !important; border: none !important; font-family: inherit; }
        .react-calendar__navigation { margin-bottom: 20px; height: 48px; }
        .react-calendar__navigation button { min-width: 44px; background: none; border: none; font-size: 16px; font-weight: 700; color: ${DARK}; border-radius: 8px; }
        .react-calendar__navigation button:enabled:hover, .react-calendar__navigation button:enabled:focus { background-color: #f1f5f9; }
        .react-calendar__month-view__weekdays { text-transform: uppercase; font-size: 11px; font-weight: 800; color: ${SECONDARY}; padding-bottom: 10px; }
        .react-calendar__tile { 
            height: 74px !important; 
            border-radius: 12px !important; 
            transition: all 0.2s ease-in-out; 
            border: 2px solid #fff !important;
            font-weight: 600;
            font-size: 14px;
            color: ${DARK};
        }
        .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus { background-color: #f1f5f9 !important; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .react-calendar__tile--now { background: #f1f5f9 !important; border: 2px solid ${PRIMARY} !important; }
        
        .present-tile { background: #e8f5e9 !important; color: #2e7d32 !important; }
        .absent-tile { background: #ffebee !important; color: #d32f2f !important; }
        .halfday-tile { background: #e3f2fd !important; color: #1976d2 !important; }
        .late-tile { background: #fff3e0 !important; color: #ed6c02 !important; }
      `}</style>
    </Box>
  );
}

export default Dashboard;